import React from "react";
import { DownloadIcon } from "assets";
import {
  withRouter,
  RouterProps,
  SectionContainer,
  Button,
  StickyCTABar,
} from "component-library";
import diff from "json-patch-gen";
import { IAudioModel } from "shared-types";
import { withTranslation as withI18nTranslation, WithTranslation } from "i18n";
import {
  captureExceptionEvent,
  FEATURE_FLAGS,
  withLaunchDarklyConsumerHOC,
  LaunchDarklyClientWithFlags,
} from "shared-services";
import { AUDIO_FULL_PATH, MEDIA_FULL_PATH } from "app/config/routes";
import {
  Breadcrumbs,
  IBreadcrumbsContent,
} from "components/common/Breadcrumbs";
import {
  withTranslation,
  ITranslationContextProps,
  Translator,
} from "contexts/TranslationContext";
import {
  withGlobalContext,
  IGlobalContextProps,
} from "shared/contexts/GlobalContext";
import { GlobalEventTypes } from "contexts/GlobalContext";
import { AlertConfigBuilder } from "shared/utils/alert-config-builder";
import {
  IEditAudioPageContent,
  getSectionContainerProps,
  IEditAudioPageContentKeys,
} from "./EditAudioPageContent";
import { EditAudioForm } from "./components/EditAudioForm";
import { AudioPlayerContainer } from "mediaModules/media/components/AudioPlayerContainer";
import { MediaAsset, MediaAssetType } from "mediaModules/media/models";
import mediaService from "mediaModules/media/services/MediaService";
import { scrollRefToTop } from "mediaModules/media/utils/page";

import { IActionButtonProps } from "component-library/src/components/Atoms/ActionButton/ActionButton";
import { IBreadcrumbsContentKeys } from "components/common/Breadcrumbs/Breadcrumbs";
import { trackMediaFileEdit } from "app/helpers/heapTracking";

import "./EditAudioPage.scss";

export interface IEditAudioPageProps
  extends
    ITranslationContextProps,
    IGlobalContextProps,
    RouterProps,
    LaunchDarklyClientWithFlags,
    WithTranslation {}

export interface IEditAudioPageState {
  content: IEditAudioPageContent;
  audioModel: MediaAsset;
  isMediaMetadataLoading: boolean;
}

export class EditAudioPage extends React.Component<
  IEditAudioPageProps,
  IEditAudioPageState
> {
  private pageRef: React.RefObject<HTMLDivElement> =
    React.createRef<HTMLDivElement>();
  private initialModelSnapshot: string;

  constructor(props: Readonly<IEditAudioPageProps>) {
    super(props);

    this.state = {
      content: EditAudioPage.mapTranslation(props.translator),
      audioModel: { ...new MediaAsset(), isMediaObjectLoading: true },
      isMediaMetadataLoading: true,
    };
  }

  public render() {
    const { content, audioModel, isMediaMetadataLoading } = this.state;
    const { flags } = this.props;

    const breadcrumbsContent: IBreadcrumbsContent =
      this.getBreadcrumbsTranslation();
    const sectionContainerProps = getSectionContainerProps(
      content,
      this.handleCloseClick
    );

    return (
      <div ref={this.pageRef} className="flex h-full flex-col justify-between">
        <div>
          <Breadcrumbs content={breadcrumbsContent} />
          <div className="g-content-area p-edit-audio-page">
            <div className="g-col-lg-12 p-edit-audio-page__container">
              <SectionContainer {...sectionContainerProps}>
                <div className="p-edit-audio-page__content">
                  <EditAudioForm
                    key={audioModel.id}
                    content={content.editAudioForm}
                    audioModel={audioModel}
                    changeModel={this.changeModel}
                    saveChanges={this.saveChanges}
                    showMediaPopup={this.showMediaPopup}
                    isMediaMetadataLoading={isMediaMetadataLoading}
                  >
                    {this.renderFormButtons}
                  </EditAudioForm>
                </div>
              </SectionContainer>
            </div>
          </div>
        </div>
        {flags[FEATURE_FLAGS.ProfileMediaPageConsolidation] &&
          this.renderStickyCTABar()}
      </div>
    );
  }

  public static getDerivedStateFromProps(
    nextProps: IEditAudioPageProps,
    prevState: IEditAudioPageState
  ) {
    if (
      nextProps.translator.version !==
      nextProps.translator.getTranslationVersion(prevState.content)
    ) {
      const content = EditAudioPage.mapTranslation(nextProps.translator);

      return { content };
    }

    return null;
  }

  public async componentDidMount() {
    try {
      await this.getAudioMetadata();
    } catch (error) {
      captureExceptionEvent(error);
    }
  }

  private renderFormButtons = (
    submit: () => void
  ): React.ReactElement | null => {
    const { flags } = this.props;

    if (flags[FEATURE_FLAGS.ProfileMediaPageConsolidation]) {
      return null;
    }

    const {
      content,
      audioModel: { isMediaObjectLoading },
    } = this.state;

    return (
      <div className="p-edit-audio-page__buttons">
        <div className="p-edit-audio-page__button">
          <Button
            type="primary"
            onClick={() => this.handleSaveClick(submit)}
            text={content.saveButton}
            disabled={isMediaObjectLoading}
          />
        </div>
        <div className="p-edit-audio-page__button">
          <Button
            type="secondary"
            onClick={this.handleCancelAndCloseClick}
            text={content.cancelButton}
          />
        </div>
      </div>
    );
  };

  private renderStickyCTABar = () => {
    const { audioModel } = this.state;
    const { t } = this.props;

    return (
      <StickyCTABar
        isSticky
        cancel={{
          text: t("common:button.label.cancel"),
          onClick: this.handleCancelAndCloseClick,
          wrapperClassName: "mr-auto",
        }}
        next={{
          text: t("common:button.label.save"),
          onClick: () => this.handleSaveClick(this.saveChanges),
          disabled: audioModel.isMediaObjectLoading,
        }}
        ctaBarAdditionalClassName="max-w-[1410px]"
      />
    );
  };

  private changeModel = (audioModel: MediaAsset) =>
    this.setState({ audioModel });

  private setInitialModelSnapshot = (audioModelUpdated: MediaAsset) => {
    this.initialModelSnapshot = JSON.stringify(audioModelUpdated);
  };

  private isModelChanged = () =>
    this.initialModelSnapshot !== JSON.stringify(this.state.audioModel);

  private async getAudioMetadata() {
    const mediaMetadata = await mediaService.getMediaMetadataById(
      this.props.router.params.id!
    );
    const audioModel = mediaService.createMediaAssetFromMeta(mediaMetadata);

    this.setState({ audioModel, isMediaMetadataLoading: false });

    await this.getAudioObject(audioModel);
  }

  private async getAudioObject(audioModel: MediaAsset) {
    const mediaObject = await mediaService.getMediaObjectById(
      audioModel.mediaInfoId
    );
    const audioModelUpdated = mediaService.mapUpdatedMediaObject(
      audioModel,
      mediaObject
    );

    this.setState({ audioModel: audioModelUpdated });
    this.setInitialModelSnapshot(audioModelUpdated);
  }

  private handleSaveClick = (submit: () => void) => {
    submit();
  };

  private handleCancelAndCloseClick = () => {
    this.closePage();
  };

  private handleCloseClick = () => {
    this.closePage();
  };

  private closePage = () => {
    const {
      content: { cancelPopup },
    } = this.state;
    const { globalContext } = this.props;

    if (this.isModelChanged()) {
      const message = new AlertConfigBuilder()
        .initBuildEntity()
        .setDefaultContent({
          closeButtonText: cancelPopup.cancelPopupContinueEditingButton,
          description: cancelPopup.cancelPopupDescription,
          title: cancelPopup.cancelPopupTitle,
        })
        .setCloseButton({
          type: "secondary",
          click: () =>
            globalContext.notifyListener(GlobalEventTypes.closeAllGlobalAlert),
        })
        .addButton({
          type: "primary",
          click: () => {
            globalContext.notifyListener(GlobalEventTypes.closeAllGlobalAlert);
            this.redirectToEditMediaAssets();
          },
          name: cancelPopup.cancelPopupCloseButton,
        })
        .build();

      globalContext.notifyListener(
        GlobalEventTypes.notifyingGlobalAlert,
        message
      );
    } else {
      this.redirectToEditMediaAssets();
    }
  };

  private saveChanges = async () => {
    const initialModel = JSON.parse(this.initialModelSnapshot);
    const { title } = this.state.audioModel;

    const difference = diff(
      {
        title: initialModel.title,
      },
      {
        title,
      }
    );

    if (difference.length) {
      try {
        await mediaService.updateMediaMetadata(
          this.props.router.params.id!,
          difference
        );
      } catch (error) {
        captureExceptionEvent(error);
      }
    }

    this.redirectToEditMediaAssets();
    trackMediaFileEdit(MediaAssetType.Audio);
  };

  private redirectToEditMediaAssets = () => {
    const { flags } = this.props;
    scrollRefToTop(this.pageRef);

    if (flags[FEATURE_FLAGS.ProfileMediaPageConsolidation]) {
      this.props.router.navigate(MEDIA_FULL_PATH);
    } else {
      this.props.router.navigate(AUDIO_FULL_PATH);
    }
  };

  private showMediaPopup = (audioModel: MediaAsset) => {
    const { id, title, sources = [], durationInMs } = audioModel;
    const controls = this.getPlayerPanel(audioModel);

    const currentTrack: IAudioModel = {
      id,
      fileName: title,
      src: sources[0],
      duration: durationInMs,
    };

    const audioPlayer = (
      <AudioPlayerContainer
        closeAction={this.closeMediaPopup}
        playerConfig={{
          currentTrack,
          isAutoPlay: true,
          tracks: [],
          selectTrack: () => undefined,
        }}
        controlPanelButtons={controls}
      />
    );

    const popupProps = {
      hideCloseButton: true,
      fullHeight: true,
    };

    this.props.globalContext.notifyListener(GlobalEventTypes.showPopup, {
      content: audioPlayer,
      popupProps,
    });
  };

  private getPlayerPanel = (mediaAsset: MediaAsset) => {
    const { downloadButton } = this.state.content.editAudioForm;

    const buttons: Array<IActionButtonProps & { key: string | number }> = [];
    buttons.push({
      key: "download1",
      svgIcon: <DownloadIcon />,
      label: downloadButton,
      onClick: () => {
        window.location.href = mediaAsset.downloadLink;
        this.props.globalContext.notifyListener(GlobalEventTypes.showPopup, {
          content: null,
        });
      },
    });

    return buttons;
  };

  private closeMediaPopup = () => {
    this.props.globalContext.notifyListener(GlobalEventTypes.showPopup, {
      content: null,
    });
  };

  private getBreadcrumbsTranslation = () =>
    this.props.translator
      .getTranslator("breadcrumbs", true)
      .createTranslationObject<IBreadcrumbsContent>(IBreadcrumbsContentKeys);

  private static mapTranslation = (translator: Translator) =>
    translator.compileTranslationObject<IEditAudioPageContent>(
      IEditAudioPageContentKeys,
      "common"
    );
}

export default withI18nTranslation()(
  withTranslation(
    withGlobalContext(withLaunchDarklyConsumerHOC()(withRouter(EditAudioPage))),
    "editAudioClipPage"
  )
) as React.ComponentType;
